import { Fragment, useEffect, useState } from "react";
import Turnstile from "./Turnstile";

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
}

const turnstileEnabled = Boolean(import.meta.env.VITE_TURNSTILE_SITE_KEY);

export default function ContactModal({ open, onClose }: ContactModalProps) {
  const [sent, setSent] = useState(false);
  const [growth, setGrowth] = useState("");
  const [otherIndustry, setOtherIndustry] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "error">("idle");
  const [token, setToken] = useState("");

  useEffect(() => {
    if (open) {
      setSent(false);
      setGrowth("");
      setOtherIndustry(false);
      setStatus("idle");
      setToken("");
    }
  }, [open]);

  const pushToDataLayer = (payload: DataLayerEvent) => {
    if (typeof window === "undefined") return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  // Block submit until the Turnstile challenge is solved (when it's configured).
  const canSubmit = (!turnstileEnabled || Boolean(token)) && status !== "sending";

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    pushToDataLayer({
        event: `lead_submit`
    });

    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload: Record<string, string> = {};
    fd.forEach((value, key) => {
      payload[key] = typeof value === "string" ? value : "";
    });
    payload.growth = growth;
    payload.turnstileToken = token;

    setStatus("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setStatus("idle");
      setSent(true);
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="cs-overlay" data-open={open} onClick={onClose}>
      <div className="cs-panel cf" onClick={(e) => e.stopPropagation()}>
        <button className="x" onClick={onClose} aria-label="Close">
          ✕
        </button>
        {sent ? (
          <div className="cf-success">
            <span className="eyebrow">Message received</span>
            <h3>Thanks — we&rsquo;ll be in touch.</h3>
            <p>
              One of our team will review your numbers and reach out within one
              business day to map the opportunity. Talk soon.
            </p>
          </div>
        ) : (
          <Fragment>
            <span className="eyebrow">Begin a partnership</span>
            <h3>Let&rsquo;s talk performance.</h3>
            <p className="cf-intro">
              Tell us where you want to take the business. The more you share, the
              sharper our first conversation.
            </p>
            <form className="cf-form" onSubmit={submit}>
              {/* Honeypot — hidden from users; bots that fill it are rejected server-side. */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}
              >
                <label>Company website</label>
                <input type="text" name="company_website" tabIndex={-1} autoComplete="off" />
              </div>

              <div className="cf-grid">
                <div className="cf-field">
                  <label>Name <span className="req">*</span></label>
                  <input type="text" name="name" required maxLength={80} placeholder="Your name" />
                </div>
                <div className="cf-field">
                  <label>Email <span className="req">*</span></label>
                  <input type="email" name="email" required maxLength={120} placeholder="you@company.com" />
                </div>
              </div>
              <div className="cf-grid">
                <div className="cf-field">
                  <label>Industry</label>
                  {otherIndustry ? (
                    <div className="cf-other-wrap">
                      <input
                        type="text"
                        name="industryOther"
                        maxLength={80}
                        autoFocus
                        placeholder="Specify your industry"
                      />
                      <button
                        type="button"
                        className="cf-other-x"
                        aria-label="Back to industry list"
                        onClick={() => setOtherIndustry(false)}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <select
                      name="industry"
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value === "Other") setOtherIndustry(true);
                      }}
                    >
                      <option value="" disabled>Select industry</option>
                      <option>Insurance</option>
                      <option>Financing</option>
                      <option>Legal</option>
                      <option>E-Commerce</option>
                      <option>Energy</option>
                      <option>Home Services</option>
                      <option>Business Sales</option>
                      <option>Market Research</option>
                      <option>Other</option>
                    </select>
                  )}
                </div>
                <div className="cf-field">
                  <label>Company size</label>
                  <select name="companySize" defaultValue="">
                    <option value="" disabled>Select size</option>
                    <option>1–10</option>
                    <option>11–50</option>
                    <option>51–200</option>
                    <option>201–500</option>
                    <option>500+</option>
                  </select>
                </div>
              </div>
              <div className="cf-grid">
                <div className="cf-field">
                  <label>Monthly acquisition target</label>
                  <input type="text" name="acqTarget" maxLength={80} placeholder="e.g. 500 leads / month" />
                </div>
                <div className="cf-field">
                  <label>CRM</label>
                  <input type="text" name="crm" maxLength={80} placeholder="e.g. HubSpot, Salesforce, Zoho" />
                </div>
              </div>
              <div className="cf-field">
                <label>Revenue growth target this year</label>
                <div className="cf-seg">
                  {["25%", "50%", "75%", "More than 100%"].map((o) => (
                    <button
                      type="button"
                      key={o}
                      aria-pressed={growth === o}
                      onClick={() => setGrowth(o)}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>

              {open && turnstileEnabled && (
                <Turnstile
                  onVerify={setToken}
                  onExpire={() => setToken("")}
                  onError={() => setToken("")}
                />
              )}

              <button type="submit" className="cf-submit" disabled={!canSubmit}>
                {status === "sending" ? "Sending…" : "Submit"} <span className="arrow">→</span>
              </button>
              {status === "error" && (
                <p className="cf-note" role="alert" style={{ color: "var(--red)" }}>
                  Something went wrong sending your message. Please try again.
                </p>
              )}
              <p className="cf-note">We typically respond within one business day.</p>
            </form>
          </Fragment>
        )}
      </div>
    </div>
  );
}